import {Dispatch} from 'redux';
import {isUpdateRequired} from '../../utils/appVersionCalculator';
import {Platform} from 'react-native';
import getConfig from '../../utils/getConfig';
import {reportError} from '../../utils/reportError';

// Action types
export const VERSION_CHECK_LOADING = 'VERSION_CHECK_LOADING';
export const VERSION_CHECK_SUCCESS = 'VERSION_CHECK_SUCCESS';
export const VERSION_CHECK_FAIL = 'VERSION_CHECK_FAIL';
export const VERSION_CHECK_DISMISS = 'VERSION_CHECK_DISMISS';

const CUSTOM_API_ENDPOINT = 'https://residents-api.onlinemanagement.info/api/v1/appVersions';

// State interface
export interface VersionState {
  isLoading: boolean;
  error: string | null;
  latestAppVersion: string | null;
  isUpdateRequired: boolean;
  updateMessage: string | null;
  lastCheckTime: number | null;
}

// Action interfaces
export interface VersionCheckLoading {
  type: typeof VERSION_CHECK_LOADING;
}

export interface VersionCheckSuccess {
  type: typeof VERSION_CHECK_SUCCESS;
  latestAppVersion: string;
  isUpdateRequired: boolean;
  updateMessage: string;
}

export interface VersionCheckFail {
  type: typeof VERSION_CHECK_FAIL;
  error: string;
}

export interface VersionCheckDismiss {
  type: typeof VERSION_CHECK_DISMISS;
}

export type VersionDispatchTypes =
  | VersionCheckLoading
  | VersionCheckSuccess
  | VersionCheckFail
  | VersionCheckDismiss;

const initialState: VersionState = {
  isLoading: false,
  error: null,
  latestAppVersion: null,
  isUpdateRequired: false,
  updateMessage: null,
  lastCheckTime: null,
};

// Action creators
export const dismissVersionCheck = () => ({
  type: VERSION_CHECK_DISMISS,
});

// Thunk for checking app version against API
export const checkAppVersionThunk =
  () => async (dispatch: Dispatch<VersionDispatchTypes>) => {
    dispatch({type: VERSION_CHECK_LOADING});

    try {
      // Get current version from config
      const currentVersion = getConfig('APP_VERSION');
      // Use Android versioning for both Android and Web platforms
      const platform = Platform.OS === 'ios' ? 'ios' : 'android';

      // Call custom API endpoint to GET app versions
      const response = await fetch(CUSTOM_API_ENDPOINT, {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw reportError(new Error(`API returned status ${response.status}`), {
          feature: 'version-check',
          action: 'checkAppVersionThunk',
          request: {
            method: 'GET',
            url: CUSTOM_API_ENDPOINT,
            status: response.status,
          },
        });
      }

      const result = await response.json();
      console.log('Version check API response:', result);

      // Response structure: { data: [...], pageNumber, pageSize, ... }
      // Filter by platform and get the latest active version
      let latestVersion = null;
      if (result?.data && Array.isArray(result.data)) {
        const platformVersion = result.data.find(
          (v) => v.platform === platform && v.active === true,
        );
        latestVersion = platformVersion?.appVersion;
      }

      if (!latestVersion) {
        dispatch({
          type: VERSION_CHECK_SUCCESS,
          latestAppVersion: currentVersion,
          isUpdateRequired: false,
          updateMessage: '',
        });
        return;
      }

      // Use version calculator to compare versions
      const updateRequired = isUpdateRequired(currentVersion, latestVersion);

      let updateMessage = '';
      if (updateRequired) {
        updateMessage =
          'A new version of the app is available. Please update to continue using the app.';
      }

      dispatch({
        type: VERSION_CHECK_SUCCESS,
        latestAppVersion: latestVersion,
        isUpdateRequired: updateRequired,
        updateMessage,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to check app version';
      console.error('Version check error:', error);
      reportError(error, {
        feature: 'version-check',
        action: 'checkAppVersionThunk',
        fallbackMessage: errorMessage,
      });

      dispatch({
        type: VERSION_CHECK_FAIL,
        error: errorMessage,
      });
    }
  };

// Reducer
const versionReducer = (
  state: VersionState = initialState,
  action: VersionDispatchTypes,
): VersionState => {
  switch (action.type) {
    case VERSION_CHECK_LOADING:
      return {
        ...state,
        isLoading: true,
        error: null,
      };

    case VERSION_CHECK_SUCCESS:
      return {
        ...state,
        isLoading: false,
        latestAppVersion: action.latestAppVersion,
        isUpdateRequired: action.isUpdateRequired,
        updateMessage: action.updateMessage,
        lastCheckTime: Date.now(),
        error: null,
      };

    case VERSION_CHECK_FAIL:
      return {
        ...state,
        isLoading: false,
        error: action.error,
        lastCheckTime: Date.now(),
      };

    case VERSION_CHECK_DISMISS:
      return {
        ...state,
        isUpdateRequired: false,
      };

    default:
      return state;
  }
};

export default versionReducer;
